import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { CategoryContext } from "@insite/client-framework/Components/CategoryContext";
import { HasShellContext, withIsInShell } from "@insite/client-framework/Components/IsInShell";
import {
    HasProductContext,
    ProductContext,
    ProductContextModel,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import {
    getErrorMessage,
    getProductsForProductInfoList,
} from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import { BrandStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { CartLineModel, ProductModel } from "@insite/client-framework/Types/ApiModels"; // Store Custom Code
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SkipNav, { SkipNavStyles } from "@insite/content-library/Components/SkipNav";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Icon, { IconProps } from "@insite/mobius/Icon"; // Store Custom Code
import ArrowLeft from "@insite/mobius/Icons/ArrowLeft"; // Store Custom Code
import ArrowRight from "@insite/mobius/Icons/ArrowRight"; // Store Custom Code
import ChevronDown from "@insite/mobius/Icons/ChevronDown"; // Store Custom Code
import ChevronLeft from "@insite/mobius/Icons/ChevronLeft";
import ChevronRight from "@insite/mobius/Icons/ChevronRight";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import Img, { ImgProps } from "@insite/mobius/Img";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import InjectableCss from "@insite/mobius/utilities/InjectableCss"; // Store Custom Code
import ProductCarouselProductSlim, {
    ProductCarouselProductSlimStyles,
} from "@store/Components/Marketing/ProductCarouselProductSlim"; // Store Custom Code
import { arrowButton } from "@store/Extensions/Widgets/Common/ProductCarousel"; // Store Custom Code
import loadCarouselProducts from "@store/Handlers/LoadCarouselProducts"; // Store Custom Code
import { dispatchProductImpressions } from "@store/Services/AnalyticsService";
import { COLORS, SPACING, TYPOGRAPHY } from "@store/theme/design";
import { siteWidthStyles } from "@store/theme/mixins";
import widgetModule from "@store/Widgets/Marketing/ProductCarouselOffCanvas";
import { useEmblaCarousel } from "embla-carousel/react";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

// Store Custom Code
interface OwnProps {
    id: string;
    product: ProductContextModel;
    productsToDisplay?: number;
    productsToScroll?: number;
    defaultIsOpen?: boolean;
    isQuickOrder?: boolean;
    extendedStyles?: ToggleableProductAccordionSliderStyles;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps & HasProductContext) => {
    const carouselId = `${ownProps.id}-${ownProps.productContext?.product?.id}`;
    const productInfoByProductId =
        state.components.productInfoLists.productInfoListById[carouselId]?.productInfoByProductId;
    return {
        products: getProductsForProductInfoList(state, carouselId),
        errorMessage: getErrorMessage(state, carouselId),
        pageType: getCurrentPage(state).type,
        currentCurency: state.context.session.currency?.currencyCode, // Store Custom Code
        carouselId, // Store Custom Code
        productInfoByProductId, // Store Custom Code
    };
};

const mapDispatchToProps = {
    loadCarouselProducts,
};

// Store Custom Code
type Props = ThemeProps<BaseTheme> &
    OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    ThemeProps<BaseTheme> &
    HasShellContext &
    HasProductContext; // Store Custom Code

export interface ToggleableProductAccordionSliderStyles {
    toggleIconUp?: IconProps; // Store Custom Code
    toggleIconDown?: IconProps; // Store Custom Code
    carouselTopWrapper?: InjectableCss; // Store Custom Code
    triggerActionWrapper?: InjectableCss; // Store Custom Code
    errorMessageText?: TypographyPresentationProps;
    titleText?: TypographyPresentationProps;
    skipCarouselButton?: SkipNavStyles;
    mainContainer?: GridContainerProps;
    prevArrowGridItem?: GridItemProps;
    prevArrowButton?: ButtonPresentationProps;
    nextArrowGridItem?: GridItemProps;
    nextArrowButton?: ButtonPresentationProps;
    carouselGridItem?: GridItemProps;
    carouselWrapper?: InjectableCss;
    carouselContainer?: InjectableCss;
    carouselSlidesContainer?: InjectableCss;
    carouselSlide?: InjectableCss;
    carouselSlideInner?: InjectableCss;
    carouselProductStyles?: ProductCarouselProductSlimStyles; // Store Custom Code
    label?: TypographyProps; // Store Custom Code
    sliderDisplayButton?: ButtonPresentationProps; // Store Custom Code
    thumbImg?: ImgProps; // Store Custom Code
    thumbImgWrapper?: InjectableCss; // Store Custom Code
    buttonLabelWrapper?: InjectableCss; // Store Custom Code
    componentWrapper?: InjectableCss; // Store Custom Code
}

// Store Custom Code
export const toggleableProductAccordionSliderStyles: ToggleableProductAccordionSliderStyles = {
    buttonLabelWrapper: {
        css: css`
            display: flex;
            margin: 2px 0;
            align-items: center;
            min-width: 160px;
        `,
    },
    thumbImgWrapper: {
        css: css`
            flex-wrap: wrap;
            margin: 2px 0;
            display: flex;
            align-items: center;
        `,
    },
    thumbImg: {
        css: css`
            height: auto;
            width: 52px;
            margin: 0.25em;
            border: 1px solid ${COLORS.border.base};
        `,
    },
    triggerActionWrapper: {
        css: css`
            position: relative;
            display: block;
            max-width: 100%;
            background: ${COLORS.background.alt};
            margin-right: 5%;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css``,
                    css``,
                    css`
                        margin-left: 16.66%;
                    `,
                    css`
                        margin-left: 16.66%;
                    `,
                ])}
        `,
    },
    sliderDisplayButton: {
        css: css`
            align-items: center;
            display: flex;
            text-decoration: none;
            font-weight: bold;
            padding: 7px 33px 7px 24px;
            font-size: 16px;
            max-width: 100%;

            &,
            &:hover {
                background: transparent;
                border: 0;
            }

            &:hover {
                text-decoration: underline;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            flex-direction: column;
                        `,
                        css``,
                        css`
                            flex-direction: row;
                        `,
                        css``,
                        css``,
                    ],
                    "min",
                )}
        `,
    },
    toggleIconUp: {
        src: ChevronUp,
        color: COLORS.text.base,
        size: 22,
        css: css`
            position: absolute;
            right: 5px;
            top: 50%;
            margin-top: -11px;
        `,
    },
    toggleIconDown: {
        src: ChevronDown,
        color: COLORS.text.base,
        size: 22,
        css: css`
            position: absolute;
            right: 7px;
            top: 50%;
            margin-top: -11px;
        `,
    },
    carouselTopWrapper: {
        css: css`
            background: ${COLORS.background.alt};
            padding: 0.5em;
            display: block;
            max-width: 95%;
            width: 95%;
        `,
    },
    errorMessageText: {
        color: "danger",
        css: css`
            margin-top: 20px;
        `,
    },
    titleText: {
        variant: "h2",
        size: TYPOGRAPHY.size.base,
        css: css`
            text-align: left;
            margin-bottom: 10px;
            font-size: ${TYPOGRAPHY.size.base} !important;
            color: ${COLORS.text.base};
            font-weight: normal;
            margin-top: 45px;

            span {
                font-weight: bold;
            }

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css``,
                    css`
                        padding: 0 5%;
                    `,
                    css`
                        padding: 0;
                    `,
                    css``,
                    css`
                        padding: 0;
                    `,
                ])}
        `,
    },
    mainContainer: {
        gap: 0,
        css: css`
            margin: 0;

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css``,
                        css``,
                        css`
                            width: calc(100% + 11%);
                            max-width: calc(100% + 11%);
                            margin: 0 -5.5%;
                        `,
                        css``,
                        css``,
                    ],
                    "min",
                )}
        `,
    },
    prevArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-start;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    prevArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            ${arrowButton()}
            border: 0;
            padding: 0;
        `,
        icon: {
            src: ArrowLeft,
            position: "left",
        },
    },
    nextArrowGridItem: {
        width: 1,
        align: "middle",
        css: css`
            justify-content: flex-end;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                    css`
                        flex-basis: 5%;
                        max-width: 5%;
                    `,
                ])}
        `,
    },
    nextArrowButton: {
        variant: "secondary",
        color: "text.main",
        css: css`
            ${arrowButton()}
            border: 0;
            padding: 0;
        `,
        icon: {
            src: ArrowRight,
            position: "left",
        },
    },
    carouselGridItem: {
        width: 10,
        css: css`
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                    css`
                        flex-basis: 90%;
                        max-width: 90%;
                    `,
                ])}
        `,
    },
    carouselWrapper: {
        css: css`
            position: relative;
            max-width: 100%;
            width: 100%;
        `,
    },
    carouselContainer: {
        css: css`
            @keyframes wait {
                0% {
                    opacity: 0;
                }
                100% {
                    opacity: 0;
                }
            }

            @keyframes fadeIn {
                0% {
                    opacity: 0;
                }
                100% {
                    opacity: 1;
                }
            }
            overflow: hidden;
            max-width: 100%;
            width: 100%;
            opacity: 1;
            animation: wait 250ms, fadeIn 400ms 250ms;
        `,
    },
    carouselSlidesContainer: {
        css: css`
            display: flex;
            width: calc(100% + 15px);
            background: ${COLORS.background.alt};
        `,
    },
    carouselSlide: {
        css: css`
            padding: 0;
            position: relative;
            flex: 0 0 auto;
            margin-left: 7.5px;
            margin-right: 7.5px;
            min-width: 245px;
        `,
    },
    carouselSlideInner: {
        css: css`
            width: 100%;
            height: 100%;
            position: relative;
            display: flex;
            flex-direction: column;
            background: white;
        `,
    },
    carouselProductStyles: {
        productDescriptionStyles: {
            productDetailLink: {
                css: css`
                    margin: 0;
                    font-weight: ${TYPOGRAPHY.weight.black};
                    font-size: ${TYPOGRAPHY.size.base};
                `,
            },
        },
        qtyGridItem: { width: 3 },
        addToCartButtonGridItem: { width: 9 },
        productCartGridItem: { width: 7 },
        pricingGridItem: { width: 5 },
        container: {
            gap: 5,
        },
        containerLower: {
            gap: 5,
        },

        productBreakPriceGridItem: {
            width: 12,
            css: css`
                padding: 0 !important;
                > button {
                    margin: 1em 0;
                    margin-left: auto;
                }
            `,
        },
        productQuantityBreakPricingStyles: {
            viewLink: {},
        },
    },
};

const styles = toggleableProductAccordionSliderStyles;

// Store Custom Code
// Based on ProductCarousel
const ToggleableProductAccordionSlider: React.FC<Props> = ({
    id,
    product, // Store Custom Code
    productsToDisplay,
    productsToScroll,
    defaultIsOpen, // Store Custom Code
    isQuickOrder, // Store Custom Code
    extendedStyles, // Store Custom Code
    theme,
    products,
    errorMessage,
    loadCarouselProducts,
    shellContext, // Store Custom Code
    currentCurency, // Store Custom Code
    carouselId, // Store Custom Code
    productInfoByProductId, // Store Custom Code
}) => {
    const [styles] = React.useState(() => mergeToNew(toggleableProductAccordionSliderStyles, extendedStyles));
    const afterCarousel = React.createRef<HTMLSpanElement>();
    const brand = React.useContext(BrandStateContext).value;
    const category = React.useContext(CategoryContext);
    const productContext = React.useContext(ProductContext);
    const numberOfProductsToDisplay = productsToDisplay || 3; // Store Custom Code
    const currentSelectedProduct = product; // Store Custom Code

    // Store Custom Code
    React.useEffect(() => {
        // Store Custom Code
        loadCarouselProducts({
            isShellEdit: shellContext.isInShell && shellContext.isEditing,
            carouselId, // Store Custom Code
            carouselType: "relatedProducts",
            relatedProductType: "Replacement",
            seedWithManuallyAssigned: "",
            displayProductsFrom: "allCategories",
            selectedCategoryIds: [],
            numberOfProductsToDisplay,
            isProductDetailsPage: false,
            productId: product.productInfo.productId,
            isProductListPage: false,
            category: undefined,
            isBrandDetailsPage: false,
            brand: undefined,
        });
    }, [productContext?.product?.id, category?.id, brand?.id]);

    const [emblaRef, embla] = useEmblaCarousel();
    const [windowResizeTime, setWindowResizeTime] = React.useState(0);
    const [canScrollPrev, setCanScrollPrev] = React.useState(false);
    const [canScrollNext, setCanScrollNext] = React.useState(false);
    const [dispatchedProduct, setDispatchedProduct] = React.useState(false); // Store Custom Code
    const setCanScroll = () => {
        setCanScrollPrev(!!embla && embla.canScrollPrev());
        setCanScrollNext(!!embla && embla.canScrollNext());
    };

    // Store Custom Code
    const addRealTimeDataToProducts = (products: ProductModel[]) => {
        const productsWithRealTimePricing: ProductModel[] = [];
        if (productInfoByProductId) {
            products.forEach(product => {
                const productInfo = productInfoByProductId[product.id];
                const unitListPrice = productInfo?.pricing?.unitListPrice ? productInfo?.pricing?.unitListPrice : 0;
                const unitListPriceDisplay = productInfo?.pricing?.unitListPriceDisplay
                    ? productInfo?.pricing?.unitListPriceDisplay
                    : "";
                const newobj: ProductModel = { ...product, unitListPrice, unitListPriceDisplay };
                productsWithRealTimePricing.push(newobj);
            });
        }
        return productsWithRealTimePricing.length > 0 ? productsWithRealTimePricing : products;
    };

    // Store Custom Code
    const getSlidesToScroll = React.useCallback(() => {
        const maxNumberOfColumns = numberOfProductsToDisplay; // Store Custom Code
        if (typeof window === "undefined") {
            return maxNumberOfColumns;
        }

        let localSlidesToScroll: number;
        if (window.innerWidth < theme.breakpoints.values[1]) {
            localSlidesToScroll = 1;
        } else if (window.innerWidth < theme.breakpoints.values[2]) {
            localSlidesToScroll = 2;
        } else if (window.innerWidth < theme.breakpoints.values[3]) {
            localSlidesToScroll = 2;
        } else {
            localSlidesToScroll = 3;
        }
        return Math.min(localSlidesToScroll, maxNumberOfColumns);
    }, []);

    const [slidesToScroll, setSlidesToScroll] = React.useState(productsToScroll || getSlidesToScroll());

    const getDraggable = () => {
        if (typeof window === "undefined") {
            return false;
        }

        return window.innerWidth < theme.breakpoints.values[1];
    };

    const [draggable, setDraggable] = React.useState(getDraggable());
    const [isBottomMobileOpen, setIsBottomMobileOpen] = React.useState(defaultIsOpen); // Store Custom Code

    // Store Custom Code
    const toggleExpanded = (
        e: React.MouseEvent<Element, MouseEvent> | React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    ) => {
        e.stopPropagation();
        setIsBottomMobileOpen(isBottomMobileOpen => !isBottomMobileOpen);
        if (products) {
            dispatchProductImpressions(
                addRealTimeDataToProducts(products),
                currentCurency,
                "ToggleableProductAccordionSliderCarousel",
            );
            setDispatchedProduct(true);
        }
    };

    React.useEffect(() => {
        const newSlidesToScroll = productsToScroll || getSlidesToScroll();
        setSlidesToScroll(newSlidesToScroll);

        const newDraggable = getDraggable();
        setDraggable(newDraggable);
    }, [windowResizeTime]); // Store Custom Code

    React.useEffect(() => {
        if (!embla) {
            return;
        }

        embla.on("init", setCanScroll);
        embla.on("select", setCanScroll);

        const onWindowResize = () => {
            setWindowResizeTime(Date.now());
        };

        window.addEventListener("resize", onWindowResize);
        onWindowResize();

        return () => {
            window.removeEventListener("resize", onWindowResize);
        };
    }, [embla]);

    React.useEffect(() => {
        if (!embla || !products || products.length === 0) {
            return;
        }

        // Store Custom Code
        // Manage display flicker since the slider is initially not in the dom
        setTimeout(() => {
            embla.reInit({
                align: "start",
                slidesToScroll,
                draggable,
                loop: products.length > slidesToScroll,
            });
            setCanScroll();
        }, 250);
    }, [embla, slidesToScroll, draggable, productInfoByProductId, products]);

    if (errorMessage) {
        return (
            <Typography {...styles.errorMessageText} as="p">
                {`Failed to load carousel products. Error: '${errorMessage}'. Please check widget settings.`}
            </Typography>
        );
    }

    if (!products || products.length === 0) {
        return null;
    }

    const showBrandBlocks = products.some(o => !!o.brand);

    return (
        <StyledWrapper {...styles.componentWrapper}>
            <StyledWrapper {...styles.triggerActionWrapper}>
                <Button
                    {...styles.sliderDisplayButton}
                    onClick={e => {
                        toggleExpanded(e);
                    }}
                >
                    <StyledWrapper {...styles.buttonLabelWrapper}>
                        {translate("Alternative parts to consider")} ({products.length})
                    </StyledWrapper>
                    <StyledWrapper {...styles.thumbImgWrapper}>
                        {products?.map(product => (
                            <Img
                                {...styles.thumbImg}
                                src={product.smallImagePath ? product.smallImagePath : "/userfiles/logogirardin.jpg"}
                                key={product.id + product.smallImagePath}
                                loading="eager"
                            />
                        ))}
                    </StyledWrapper>
                    {isBottomMobileOpen && (
                        <Icon
                            onClick={e => {
                                e.preventDefault();
                            }}
                            {...styles.toggleIconUp}
                        />
                    )}
                    {!isBottomMobileOpen && (
                        <Icon
                            onClick={e => {
                                e.preventDefault();
                            }}
                            {...styles.toggleIconDown}
                        />
                    )}
                </Button>
            </StyledWrapper>
            {isBottomMobileOpen && (
                <StyledWrapper {...styles.carouselTopWrapper}>
                    <SkipNav
                        text={translate("Skip Carousel")}
                        extendedStyles={styles.skipCarouselButton}
                        destination={afterCarousel}
                    />
                    <GridContainer {...styles.mainContainer} data-test-selector="ToggleableProductAccordionSlider_cart">
                        <GridItem {...styles.prevArrowGridItem}>
                            {products.length > slidesToScroll && (
                                <Button
                                    {...styles.prevArrowButton}
                                    onClick={() => embla && embla.scrollPrev()}
                                    disabled={!canScrollPrev}
                                    data-test-selector="prevBtn"
                                >
                                    <ButtonIcon src={ChevronLeft} />
                                </Button>
                            )}
                        </GridItem>
                        <GridItem {...styles.carouselGridItem}>
                            <StyledWrapper {...styles.carouselWrapper}>
                                <StyledWrapper {...styles.carouselContainer} ref={emblaRef}>
                                    <StyledWrapper {...styles.carouselSlidesContainer} data-test-selector="slides">
                                        {products.map(product => (
                                            <StyledWrapper
                                                {...styles.carouselSlide}
                                                key={product.id}
                                                style={{ width: `calc(100% / ${slidesToScroll} - 15px)` }}
                                            >
                                                <StyledWrapper
                                                    {...styles.carouselSlideInner}
                                                    id={`product_${product.id}`}
                                                    data-test-selector="productContainer"
                                                >
                                                    <ProductCarouselProductSlim
                                                        carouselId={carouselId}
                                                        product={product}
                                                        showImage={true}
                                                        showBrand={showBrandBlocks}
                                                        showTitle={true}
                                                        showPartNumbers={true}
                                                        showAvailability={true}
                                                        showPrice={true}
                                                        showAddToCart={true}
                                                        showAddToList={true}
                                                        desktopAddOverride={translate("Add")}
                                                        mobileAddOverride={translate("Add")}
                                                        extendedStyles={styles.carouselProductStyles}
                                                        isQuickOrder={isQuickOrder}
                                                        parentProduct={currentSelectedProduct}
                                                    />
                                                </StyledWrapper>
                                            </StyledWrapper>
                                        ))}
                                    </StyledWrapper>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                        <GridItem {...styles.nextArrowGridItem}>
                            {products.length > slidesToScroll && (
                                <Button
                                    {...styles.nextArrowButton}
                                    onClick={() => embla && embla.scrollNext()}
                                    disabled={!canScrollNext}
                                    data-test-selector="nextBtn"
                                >
                                    <ButtonIcon src={ChevronRight} />
                                </Button>
                            )}
                        </GridItem>
                    </GridContainer>
                    <span ref={afterCarousel} tabIndex={-1} />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default withProductContext(
    connect(mapStateToProps, mapDispatchToProps)(withIsInShell(withTheme(ToggleableProductAccordionSlider))),
);
